import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, money, STATUSES, STATUS_COLORS, groupActivitiesByCategory } from "@/hub/lib/api";
import { exportGroupPDF } from "@/hub/lib/pdf";
import { Plus, Users, FileDown, Trash2, Phone, Mail, User, Pencil, X, MapPin, Mountain, CalendarClock, BedDouble, Wallet, Utensils } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetFooter } from "@/components/ui/sheet";
import { BookingSheet } from "@/hub/components/BookingSheet";
import { toast } from "sonner";

const emptyForm = {
  name: "", contact_person: "", email: "", phone: "", size: "", status: "Lead",
  event_date: "", event_end_date: "", package_id: "", rm_name: "", notes: "",
};

export default function Groups() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [packages, setPackages] = useState([]);
  const [activities, setActivities] = useState([]);
  const [categories, setCategories] = useState([]);
  const [detail, setDetail] = useState(null);
  const [filter, setFilter] = useState("All");
  const [filterType, setFilterType] = useState("All");
  const [filterZone, setFilterZone] = useState("All");
  const [filterMonth, setFilterMonth] = useState("");

  const loadGroups = () => api.groups().then(setGroups);

  useEffect(() => {
    loadGroups();
    api.packages().then(setPackages);
    api.activities().then(setActivities);
    api.categories().then(setCategories);
  }, []);

  useEffect(() => {
    if (id) api.group(id).then(setDetail);
    else setDetail(null);
  }, [id, groups]);

  const zoneOptions = [...new Set(groups.flatMap((g) => g.booking_zones || []))].sort();
  const filtered = groups
    .filter((g) => (filter === "All" || g.status === filter))
    .filter((g) => (filterType === "All" || (g.booking_types || []).includes(filterType)))
    .filter((g) => (filterZone === "All" || (g.booking_zones || []).includes(filterZone)))
    .filter((g) => (!filterMonth || (g.event_date || "").slice(0, 7) === filterMonth))
    .sort((a, b) => {
      if (!a.event_date) return 1;
      if (!b.event_date) return -1;
      return a.event_date.localeCompare(b.event_date);
    });

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="lg:col-span-1 bg-white border border-border h-fit">
        <div className="p-4 border-b border-border flex items-center justify-between">
          <h3 className="font-heading text-lg font-medium flex items-center gap-2"><Users className="h-4 w-4" /> Clients</h3>
          <GroupFormSheet packages={packages} onDone={loadGroups} />
        </div>
        <div className="p-3 border-b border-border grid grid-cols-2 gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="rounded-sm h-8 text-xs" data-testid="group-filter"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="All">All Statuses</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="rounded-sm h-8 text-xs" data-testid="group-filter-type"><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="All">All Types</SelectItem>
              <SelectItem value="Leisure">Leisure</SelectItem>
              <SelectItem value="Training">Training</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterZone} onValueChange={setFilterZone}>
            <SelectTrigger className="rounded-sm h-8 text-xs" data-testid="group-filter-zone"><SelectValue placeholder="Zone" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="All">All Zones</SelectItem>
              {zoneOptions.map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}
            </SelectContent>
          </Select>
          <input type="month" value={filterMonth} onChange={(e) => setFilterMonth(e.target.value)} className="rounded-sm h-8 text-xs border border-input px-2 bg-white" data-testid="group-filter-month" />
        </div>
        <div className="divide-y divide-border max-h-[70vh] overflow-auto lush-scroll">
          {filtered.length === 0 && <div className="p-8 text-center text-xs text-stone-400 border border-dashed border-stone-200 m-4">No groups yet. Add your first client group.</div>}
          {filtered.map((g) => (
            <button key={g.id} onClick={() => navigate(`/hub/groups/${g.id}`)} data-testid={`group-row-${g.id}`}
              className={`w-full text-left p-4 hover:bg-stone-50 transition-colors ${id === g.id ? "bg-forest/5 border-l-2 border-l-forest" : ""}`}>
              <div className="flex items-center justify-between">
                <span className="font-medium text-sm">{g.name}</span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded-sm font-semibold ${STATUS_COLORS[g.status]}`}>{g.status}</span>
              </div>
              <div className="text-xs text-stone-400 mt-1 flex justify-between">
                <span>{g.size} pax · {g.event_date || "no date"}</span>
                <span className="font-mono text-forest">{money(g.total)}</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="lg:col-span-2">
        {!detail ? (
          <div className="bg-white border border-dashed border-stone-300 h-full min-h-[400px] flex items-center justify-center text-stone-400 text-sm">
            Select a client group to view and manage their arrangement.
          </div>
        ) : (
          <GroupDetail detail={detail} packages={packages} activities={activities} categories={categories} groups={groups}
            onRefresh={() => { api.group(detail.id).then(setDetail); loadGroups(); }}
            onDeleted={() => { navigate("/hub/groups"); loadGroups(); }} />
        )}
      </div>
    </div>
  );
}

function GroupDetail({ detail, packages, activities, categories, groups, onRefresh, onDeleted }) {
  const [addonActivity, setAddonActivity] = useState("");
  const [addonQty, setAddonQty] = useState("1");
  const catOrder = Object.fromEntries((categories || []).map((c, i) => [c.name, c.order ?? i]));
  const groupActs = (items) => groupActivitiesByCategory(items || [], activities, catOrder);

  const saveAddons = async (add_ons) => {
    await api.updateGroup(detail.id, {
      name: detail.name, contact_person: detail.contact_person, email: detail.email, phone: detail.phone,
      size: detail.size, status: detail.status, event_date: detail.event_date, event_end_date: detail.event_end_date,
      package_id: detail.package_id, rm_name: detail.rm_name, notes: detail.notes, add_ons,
    });
    onRefresh();
  };

  const addAddon = async () => {
    const act = activities.find((a) => a.id === addonActivity);
    if (!act) return toast.error("Pick an activity");
    const add_ons = [...(detail.add_ons || []), { activity_id: act.id, name: act.name, price: act.cost, qty: Number(addonQty) || 1 }];
    await saveAddons(add_ons);
    toast.success(`${act.name} added`);
    setAddonActivity(""); setAddonQty("1");
  };

  const removeAddon = async (idx) => {
    const add_ons = detail.add_ons.filter((_, i) => i !== idx);
    await saveAddons(add_ons);
    toast.success("Add-on removed");
  };

  const addonTotal = (detail.add_ons || []).reduce((s, a) => s + a.price * a.qty, 0);

  return (
    <div className="space-y-4">
      <div className="bg-white border border-border p-6">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3">
              <h2 className="font-heading text-2xl font-semibold tracking-tight">{detail.name}</h2>
              <span className={`text-xs px-2 py-0.5 rounded-sm font-semibold ${STATUS_COLORS[detail.status]}`}>{detail.status}</span>
            </div>
            <div className="flex flex-wrap gap-x-5 gap-y-1 mt-3 text-sm text-stone-500">
              <span className="flex items-center gap-1.5"><User className="h-3.5 w-3.5" /> {detail.contact_person || "—"}</span>
              <span className="flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" /> {detail.phone || "—"}</span>
              <span className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> {detail.email || "—"}</span>
              <span className="flex items-center gap-1.5"><Users className="h-3.5 w-3.5" /> {detail.size} pax</span>
            </div>
            <div className="text-xs text-stone-400 mt-2">
              Event: {detail.event_date || "—"}{detail.event_end_date ? ` → ${detail.event_end_date}` : ""} · RM: {detail.rm_name || "—"}
            </div>
          </div>
          <div className="flex gap-2">
            <BookingSheet defaultGroupId={detail.id} groups={groups} packages={packages} activities={activities} onDone={onRefresh}
              trigger={<Button size="sm" className="rounded-sm bg-terracotta hover:bg-terracotta-hover" data-testid="new-booking-btn"><CalendarClock className="h-3.5 w-3.5 mr-1" /> New Booking</Button>} />
            <GroupFormSheet packages={packages} existing={detail} onDone={onRefresh} trigger={
              <Button variant="outline" size="sm" className="rounded-sm" data-testid="edit-group-btn"><Pencil className="h-3.5 w-3.5 mr-1" /> Edit</Button>
            } />
            <Button size="sm" className="rounded-sm bg-forest hover:bg-forest-hover" data-testid="export-pdf-btn" onClick={() => exportGroupPDF(detail)}>
              <FileDown className="h-3.5 w-3.5 mr-1" /> Quote PDF
            </Button>
            <Button variant="outline" size="icon" className="rounded-sm h-9 w-9 text-red-500 hover:bg-red-50" data-testid="delete-group-btn"
              onClick={() => { if (window.confirm("Delete this group?")) api.deleteGroup(detail.id).then(() => { toast.success("Group deleted"); onDeleted(); }); }}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white border border-border p-5">
          <h3 className="font-heading text-base font-medium mb-3">Package & Pricing</h3>
          <div className="flex justify-between items-center py-2 border-b border-border">
            <span className="text-sm">{detail.package_name || "No base package"}</span>
            <span className="font-mono text-sm">{money(detail.package_price)}</span>
          </div>
          {(detail.add_ons || []).map((a, i) => (
            <div key={i} className="flex justify-between items-center py-2 border-b border-border group" data-testid={`addon-row-${i}`}>
              <span className="text-sm text-stone-600">{a.name} <span className="text-xs text-stone-400">×{a.qty}</span></span>
              <div className="flex items-center gap-2">
                <span className="font-mono text-sm text-stone-500">{money(a.price * a.qty)}</span>
                <button onClick={() => removeAddon(i)} data-testid={`remove-addon-${i}`}><X className="h-3.5 w-3.5 text-stone-300 hover:text-red-500" /></button>
              </div>
            </div>
          ))}
          <div className="flex justify-between items-center py-3 mt-1 bg-forest text-white px-3 rounded-sm">
            <span className="text-sm font-semibold uppercase tracking-wider">Total</span>
            <span className="font-mono text-lg">{money(detail.package_price + addonTotal)}</span>
          </div>

          <div className="mt-4 pt-4 border-t border-dashed border-stone-200">
            <Label className="text-xs uppercase tracking-wider text-stone-400">Add Activity Add-on</Label>
            <div className="flex gap-2 mt-2">
              <Select value={addonActivity} onValueChange={setAddonActivity}>
                <SelectTrigger className="rounded-sm flex-1" data-testid="addon-activity-select"><SelectValue placeholder="Activity" /></SelectTrigger>
                <SelectContent>{activities.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} · {money(a.cost)}</SelectItem>)}</SelectContent>
              </Select>
              <Input type="number" value={addonQty} onChange={(e) => setAddonQty(e.target.value)} className="rounded-sm w-16" data-testid="addon-qty-input" />
              <Button onClick={addAddon} size="icon" className="rounded-sm bg-terracotta hover:bg-terracotta-hover shrink-0" data-testid="add-addon-btn"><Plus className="h-4 w-4" /></Button>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-white border border-border p-5">
            <h3 className="font-heading text-base font-medium mb-3 flex items-center gap-2"><MapPin className="h-4 w-4" /> Reserved Zones</h3>
            {(detail.reservations || []).length === 0 ? <p className="text-xs text-stone-400">No zones reserved. Use the Calendar to reserve.</p> :
              (detail.reservations || []).map((r) => (
                <div key={r.id} className="flex items-center gap-2 py-1.5 text-sm border-b border-border last:border-0">
                  <span className="h-3 w-3 rounded-sm" style={{ background: r.zone_color }} />
                  <span className="flex-1">{r.zone_name}</span>
                  <span className="font-mono text-xs text-stone-400">{r.date}</span>
                </div>
              ))}
          </div>
          <div className="bg-white border border-border p-5">
            <h3 className="font-heading text-base font-medium mb-3 flex items-center gap-2"><Mountain className="h-4 w-4" /> Scheduled Activities</h3>
            {(detail.scheduled_activities || []).length === 0 ? <p className="text-xs text-stone-400">Nothing scheduled. Use the Calendar to schedule.</p> :
              (detail.scheduled_activities || []).map((a) => (
                <div key={a.id} className="flex items-center gap-2 py-1.5 text-sm border-b border-border last:border-0 border-l-2 border-l-terracotta pl-2">
                  <span className="flex-1">{a.activity_name}</span>
                  <span className="font-mono text-xs text-stone-400">{a.date} {a.time}</span>
                </div>
              ))}
          </div>
        </div>
      </div>

      {(detail.bookings || []).length > 0 && (
        <div className="bg-white border border-border">
          <div className="p-5 border-b border-border">
            <h3 className="font-heading text-base font-medium flex items-center gap-2"><CalendarClock className="h-4 w-4" /> Bookings</h3>
          </div>
          <div className="divide-y divide-border">
            {detail.bookings.map((b) => (
              <div key={b.id} className="p-5" data-testid={`booking-item-${b.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-sm font-semibold uppercase tracking-wider ${b.type === "Training" ? "bg-terracotta text-white" : "bg-forest text-white"}`}>{b.type}</span>
                    <span className="text-sm font-medium">{b.package_name || "No package"}</span>
                  </div>
                  <button onClick={() => { if (window.confirm("Delete this booking? Its reservations will be removed.")) api.deleteBooking(b.id).then(() => { toast.success("Booking deleted"); onRefresh(); }); }} data-testid={`del-booking-${b.id}`}>
                    <Trash2 className="h-4 w-4 text-stone-300 hover:text-red-500 transition-colors" />
                  </button>
                </div>
                <div className="mt-3 space-y-3">
                  <BSection icon={Mountain} label="Activities">
                    {(b.additional_activities || []).length === 0 ? <span className="text-xs text-stone-400">None</span> : (
                      <div className="space-y-1.5" data-testid={`booking-activities-${b.id}`}>
                        {groupActs(b.additional_activities).map(([cat, list]) => (
                          <div key={cat} className="flex items-start gap-2 flex-wrap">
                            <span className="text-[10px] uppercase tracking-wider text-stone-400 mt-1 min-w-[80px]">{cat}</span>
                            <div className="flex flex-wrap gap-1.5">
                              {list.map((a, i) => <span key={i} className="text-[11px] bg-terracotta/10 text-terracotta px-1.5 py-0.5 rounded-sm">{a.name} ×{a.qty}</span>)}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </BSection>
                  <BSection icon={MapPin} label="Facilities">
                    <span className="text-xs text-stone-600 inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm inline-block" style={{ background: b.zone_color }} /> {b.zone_name}</span>
                  </BSection>
                  <BSection icon={BedDouble} label="Accommodation">
                    <span className="text-xs text-stone-600">{b.accommodation_required ? b.accommodation_type_name : "None"}</span>
                  </BSection>
                  <BSection icon={Utensils} label="Meal Menu">
                    {(b.meals || []).length === 0 ? <span className="text-xs text-stone-400">None</span> : (
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {b.meals.map((m) => <span key={m} className="text-[11px] bg-stone-100 text-stone-600 px-1.5 py-0.5 rounded-sm">{m}</span>)}
                      </div>
                    )}
                  </BSection>
                  <BSection icon={Wallet} label="Other">
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-stone-500">
                      <span className="inline-flex items-center gap-1.5"><CalendarClock className="h-3.5 w-3.5" /> {b.start_date} → {b.end_date}{(b.checkin_time || b.checkout_time) ? ` · ${b.checkin_time || "?"}–${b.checkout_time || "?"}` : ""}</span>
                      <span>Pax: {b.pax || "—"}</span>
                      <span>{b.advance_paid ? `Advance ${money(b.advance_amount)}` : "No advance"}</span>
                    </div>
                  </BSection>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {detail.notes && (
        <div className="bg-white border border-border p-5">
          <h3 className="font-heading text-base font-medium mb-2">Notes</h3>
          <p className="text-sm text-stone-600 whitespace-pre-wrap">{detail.notes}</p>
        </div>
      )}
    </div>
  );
}

function GroupFormSheet({ packages, existing, onDone, trigger }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    if (open) {
      setForm(existing ? {
        name: existing.name, contact_person: existing.contact_person || "", email: existing.email || "",
        phone: existing.phone || "", size: existing.size || "", status: existing.status,
        event_date: existing.event_date || "", event_end_date: existing.event_end_date || "",
        package_id: existing.package_id || "", rm_name: existing.rm_name || "", notes: existing.notes || "",
      } : { ...emptyForm, rm_name: localStorage.getItem("lush_rm_name") || "" });
    }
  }, [open, existing]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const submit = async () => {
    if (!form.name) return toast.error("Group name is required");
    const payload = {
      ...form, size: Number(form.size) || 0,
      package_id: form.package_id || null,
      event_date: form.event_date || null, event_end_date: form.event_end_date || null,
      add_ons: existing?.add_ons || [],
    };
    try {
      if (existing) await api.updateGroup(existing.id, payload);
      else await api.createGroup(payload);
      toast.success(existing ? "Group updated" : "Group created");
      setOpen(false);
      onDone();
    } catch (e) {
      toast.error("Save failed");
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        {trigger || <Button size="sm" className="rounded-sm bg-forest hover:bg-forest-hover" data-testid="new-group-btn"><Plus className="h-4 w-4 mr-1" /> New</Button>}
      </SheetTrigger>
      <SheetContent className="overflow-y-auto lush-scroll w-full sm:max-w-md">
        <SheetHeader><SheetTitle className="font-heading">{existing ? "Edit Group" : "New Client Group"}</SheetTitle></SheetHeader>
        <div className="space-y-3 mt-5">
          <Field label="Group / Company Name"><Input value={form.name} onChange={set("name")} className="rounded-sm" data-testid="gf-name" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Contact Person"><Input value={form.contact_person} onChange={set("contact_person")} className="rounded-sm" data-testid="gf-contact" /></Field>
            <Field label="Phone"><Input value={form.phone} onChange={set("phone")} className="rounded-sm" data-testid="gf-phone" /></Field>
          </div>
          <Field label="Email"><Input value={form.email} onChange={set("email")} className="rounded-sm" data-testid="gf-email" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Group Size (pax)"><Input type="number" value={form.size} onChange={set("size")} className="rounded-sm" data-testid="gf-size" /></Field>
            <Field label="Status">
              <Select value={form.status} onValueChange={set("status")}>
                <SelectTrigger className="rounded-sm" data-testid="gf-status"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Event Date"><Input type="date" value={form.event_date} onChange={set("event_date")} className="rounded-sm" data-testid="gf-date" /></Field>
            <Field label="End Date"><Input type="date" value={form.event_end_date} onChange={set("event_end_date")} className="rounded-sm" data-testid="gf-enddate" /></Field>
          </div>
          <Field label="Base Package">
            <Select value={form.package_id} onValueChange={set("package_id")}>
              <SelectTrigger className="rounded-sm" data-testid="gf-package"><SelectValue placeholder="Select package" /></SelectTrigger>
              <SelectContent>{packages.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {money(p.base_price)}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Relationship Manager"><Input value={form.rm_name} onChange={set("rm_name")} className="rounded-sm" data-testid="gf-rm" /></Field>
          <Field label="Notes"><Textarea value={form.notes} onChange={set("notes")} className="rounded-sm" rows={3} data-testid="gf-notes" /></Field>
        </div>
        <SheetFooter className="mt-5">
          <Button onClick={submit} className="rounded-sm bg-forest hover:bg-forest-hover w-full" data-testid="save-group-btn">{existing ? "Save Changes" : "Create Group"}</Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

const Field = ({ label, children }) => (
  <div>
    <Label className="text-xs uppercase tracking-wider text-stone-400 mb-1 block">{label}</Label>
    {children}
  </div>
);

const BSection = ({ icon: Icon, label, children }) => (
  <div className="flex items-start gap-2">
    <Icon className="h-3.5 w-3.5 text-stone-400 mt-0.5 shrink-0" />
    <div className="flex-1 min-w-0">
      <div className="text-[10px] uppercase tracking-[0.15em] text-stone-400 mb-1">{label}</div>
      {children}
    </div>
  </div>
);
